import { useSearchParams } from "react-router-dom";
import Loader from "../components/Loader"
import PageContainer from "../components/PageContainer"
import { useEffect, useRef, useState } from "react";
import PageTitle from "../components/PageTitle";
import OwnActivitiesCard from "../components/OwnActivitiesCard";
import Error from '../components/Error';
import CustomHr from "../components/CustomHr";
import UserRequestCard from "../components/booking/UserRequestCard";
import { Capacitor } from '@capacitor/core';
import { Browser } from '@capacitor/browser';
import { PENDING_CHECKOUT_KEY } from '../components/DeepLinkHandler.js';

const CompanyDetails = () => {

    // Catturo l'id nella query
    const [searchParams] = useSearchParams();
    const companyId = searchParams.get('companyId');
    const stripeAccountId = searchParams.get('stripeAccountId');
    const stripeStatus = searchParams.get('stripeStatus')

    // UX: Informo l'utente che i dati dell'account Stripe sono stati aggiornati
    const [isStripeAccountUpdated, setIsStripeAccountUpdated] = useState(false);
    useEffect(() => {
        if (stripeAccountId) {
            setIsStripeAccountUpdated(true);
            setTimeout(() => {
                setIsStripeAccountUpdated(false)
            }, 5000)
        }
    }, [stripeAccountId])

    // Fetch per riceere i dati dell'azienda
    const [companyData, setCompanyData] = useState(null);
    const [isLoading, setIsLoading] = useState(false);
    const [error, setError] = useState(null);
    useEffect(() => {
        if (companyId) {
            getCompanyData()
        }
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [companyId])
    const token = localStorage.getItem('ticino-wild-token');
    const getCompanyData = async () => {
        setIsLoading(true);
        let url = `${process.env.REACT_APP_SERVER_BASE_URL}/companies/get-company/${companyId}`;
        let headers = { 'Content-Type': 'application/json', Authorization: `Bearer ${token}` };
        let options = { method: 'GET', headers };
        try {
            const response = await fetch(url, options);
            if (response.ok) {
                const result = await response.json();
                setCompanyData(result.payload);
                setError(null);
                setIsLoading(false);
            } else {
                const error = await response.json();
                throw error
            }
        } catch (error) {
            setError(error);
            setCompanyData(null);
            setIsLoading(false);
        }
    }

    // Gestico i dati delle attività associate
    const [ownActivitiesData, setOwnActivitiesData] = useState([]);
    const [isActivitiesLoading, setIsActivitiesLoading] = useState(false);
    const [activitiesError, setActivitiesError] = useState(null);
    useEffect(() => {
        if (companyData) {
            if (companyData.ownActivities.length > 0) {
                ownActivitiesFetch();
            }
        }
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [companyData])

    // Fetch per ricevere le attività associate all'azienda
    const ownActivitiesFetch = async () => {
        setIsActivitiesLoading(true);
        try {
            const url = `${process.env.REACT_APP_SERVER_BASE_URL}/activities/get-activities-by-id-array`;
            const headers = {
                'Content-Type': 'application/json',
                Authorization: `Bearer ${token}`
            };
            const options = {
                method: 'POST',
                headers,
                body: JSON.stringify({ activitiesIdArray: companyData.ownActivities })
            };
            const response = await fetch(url, options);
            if (response.ok) {
                const result = await response.json();
                setOwnActivitiesData(result.payload);
                setIsActivitiesLoading(false);
            } else {
                const error = await response.json();
                throw new Error(error.message || "Errore sconosciuto nella risposta.");
            }
        } catch (error) {
            console.error("Errore durante la richiesta: ", error);
            setActivitiesError(error);
            setIsActivitiesLoading(false);
        }
    }

    // Fetch per ricevere il link onboarding Stripe per impostare i dati dell'account dell'azienda
    const [stripeLink, setStripeLink] = useState(null);
    const [stripeIsLoading, setStripeIsLoading] = useState(false);
    const [stripeError, setStripeError] = useState(null);
    const getStripeAccountLink = async () => {
        try {
            setStripeIsLoading(true);
            const url = `${process.env.REACT_APP_SERVER_BASE_URL}/companies/get-stripe-link/${companyData._id}/${companyData.stripeAccountId}`;
            const headers = { 'Content-Type': 'application/json', Authorization: `Bearer ${token}` };
            const options = { method: 'GET', headers };
            const response = await fetch(url, options);
            if (response.ok) {
                const result = await response.json();
                setStripeLink(result.accountLinkUrl);
                setStripeIsLoading(false);
            } else {
                const error = await response.json();
                throw error
            }
        } catch (error) {
            console.error(error);
            setStripeError(error.message || "Errore nel ricevere il link per stripe");
            setStripeIsLoading(false);
        }
    }

    /** Stesso meccanismo di ConfirmOrderRequest: pending + Browser.open → stripe-return → ticinowild:// / browserFinished. */
    const openStripeConnectInBrowser = async (url) => {
        if (!companyData?._id || !companyData?.stripeAccountId) return;
        localStorage.setItem(
            PENDING_CHECKOUT_KEY,
            JSON.stringify({
                type: 'connect_onboarding',
                companyId: companyData._id,
                stripeAccountId: companyData.stripeAccountId,
            })
        );
        await Browser.open({ url });
    };

    const hasVerifiedStripeStatus = useRef(false);
    useEffect(() => {
        if (stripeStatus && companyData && companyData._id && !hasVerifiedStripeStatus.current) {
            hasVerifiedStripeStatus.current = true;
            verifyAndPollStripeStatus();
        }
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [stripeStatus, companyData]);

    const verifyAndPollStripeStatus = async (attempt = 1) => {
        const maxAttempts = 5;
        const delayMs = 3000;
        try {
            const url = `${process.env.REACT_APP_SERVER_BASE_URL}/companies/verify-stripe-status/${companyData._id}`;
            const headers = { 'Content-Type': 'application/json', Authorization: `Bearer ${token}` };
            const response = await fetch(url, { method: 'GET', headers });
            if (response.ok) {
                const result = await response.json();
                if (result.stripeAccountStatus !== 'pending') {
                    setCompanyData(prev => ({ ...prev, stripeAccountStatus: result.stripeAccountStatus }));
                    setIsStripeAccountUpdated(true);
                    setTimeout(() => setIsStripeAccountUpdated(false), 5000);
                } else if (attempt < maxAttempts) {
                    setTimeout(() => verifyAndPollStripeStatus(attempt + 1), delayMs);
                }
            }
        } catch (error) {
            console.error('Stripe status verify error: ', error);
        }
    }

    // Richiedo i dati (nome) delle attività in request
    const [requestActivitiesData, setRequestActivitiesData] = useState(null);
    const [, setRequestActivitiesDataStatus] = useState('idle');
    const [, setRequestActivitiesDataError] = useState(null);
    const getRequestActivities = async (array) => {
        setRequestActivitiesDataStatus('loading');
        try {
            const url = `${process.env.REACT_APP_SERVER_BASE_URL}/activities/get-activities-data-requests`;
            const headers = {
                Authorization: `Bearer ${token}`,
                'Content-Type': 'application/json'
            };
            const options = {
                method: 'POST',
                headers,
                body: JSON.stringify({ requestsArray: array })
            };
            const response = await fetch(url, options);
            if (response.ok) {
                const result = await response.json();
                setRequestActivitiesData(result.payload);
                setRequestActivitiesDataStatus('success');
            } else {
                const error = await response.json();
                throw error;
            }
        } catch (error) {
            console.error(error.message || "Errore durante la richiesta Requests.");
            setRequestActivitiesDataError(error);
            setRequestActivitiesDataStatus('failed');
        }
    }
    useEffect(() => {
        if (companyData && companyData.requests && companyData.requests.length > 0) {
            // Ricavo la lista di id delle requests;
            const requestsIdArray = companyData.requests.map(element => {
                return { requestId: element._id, status: element.status, requestDate: element.createdAt, activityId: element.activityId }
            })
            getRequestActivities(requestsIdArray);
        }
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [companyData])

    return (
        <PageContainer desktopItems="center">
            <PageTitle backLink={"/handle-companies"} title={companyData && companyData.companyName} />
            {isLoading && <Loader />}
            {error && !isLoading && (error.message || "Errore sconosciuto nel ricevere i dati dell'azienda")}
            {
                companyData && <div className="w-full lg:w-[1000px] flex flex-col justify-center items-start lg:items-center gap-8">

                    <div className="flex flex-col gap-2 border-2 rounded-xl p-4 w-full">
                        <h4 className="text-neutral-400">Informazioni principali</h4>
                        <h3>Ragione sociale: {companyData.companyName}</h3>
                        <p>IDI: {companyData.IDI}</p>

                        <div className="flex flex-col gap-2">
                            <div>INDIRIZZO: {companyData.address.street}, {companyData.address.city} - {companyData.address.postalCode}</div>
                        </div>
                    </div>

                    <div className="flex flex-col gap-2 border-2 rounded-xl p-4 w-full">
                        <h4 className="text-neutral-400">Altri dettagli</h4>
                        <div>Telefono: {companyData.phone}</div>
                        <div>Email: {companyData.email}</div>
                        <div>Website: {companyData.website}</div>
                        <div>Note sull'azienda: {companyData.notes}</div>
                    </div>

                    {/* Dati account Stripe aziendali */}
                    {
                        companyData && companyData.stripeAccountId &&
                        <div className="flex flex-col gap-2 border-2 rounded-xl p-4 w-full">
                            {
                                isStripeAccountUpdated && <div className="w-full p-4 border-2 border-[#225424] bg-green-100 rounded-xl">
                                    Status account Stripe aggiornato: <strong>{companyData.stripeAccountStatus}</strong>
                                </div>
                            }
                            <h4 className="text-neutral-400">Account custom Stripe dell'azienda</h4>
                            <div>Id dell'account: {companyData.stripeAccountId}</div>
                            <div>Stato dell'account: {companyData.stripeAccountStatus}</div>
                            {!stripeIsLoading && !stripeError && !stripeLink && <button className="bg-[#225424] py-3 px-6 text-white rounded-lg w-full md:w-fit" onClick={getStripeAccountLink}>Genera link di impostazioni aziendali</button>}
                            {!stripeIsLoading && stripeError && !stripeLink && <Error text={stripeError} />}
                            {stripeIsLoading && <Loader />}
                            {stripeLink && <div className="flex gap-2"><div>Link Stripe</div>
  {Capacitor.isNativePlatform()
    ? <h4 className="cursor-pointer text-blue-600 underline" onClick={() => openStripeConnectInBrowser(stripeLink)}>{stripeLink}</h4>
    : <a href={stripeLink} target="_blank" rel="noopener noreferrer"><h4>{stripeLink}</h4></a>
  }
</div>}
                        </div>
                    }

                    {/* Dati aziendali da mostrare al cliente */}
                    <div className="flex flex-col gap-2 border-2 rounded-xl p-4 w-full">
                        <h4 className="text-neutral-400">{companyData.dataForPurchase && companyData.dataForPurchase.length > 0 ? 'Dati aziendali da mostrare al cliente ad acquisto avvenuto' : 'Nessun dato aziendale da mostrare al cliente impostato'}</h4>
                        <div><ul>
                            {
                                companyData.dataForPurchase && companyData.dataForPurchase.length > 0 &&
                                companyData.dataForPurchase.map((element, index) => {
                                    return <li key={index}>{element.label}</li>
                                })
                            }
                        </ul></div>
                    </div>

                    {/* Attività associate all'azienda */}
                    <div className="flex flex-col gap-2 border-2 rounded-xl p-4 w-full">
                        <h4 className="text-neutral-400">{ownActivitiesData && ownActivitiesData.length > 0 ? 'Attività associate' : 'Nessuna attività associata a questa azienda'}</h4>
                        <div className="mt-4 flex flex-col gap-2">
                            {
                                !isActivitiesLoading && !activitiesError &&
                                ownActivitiesData && ownActivitiesData.length > 0 &&
                                ownActivitiesData.map((element, index) => {
                                    return <OwnActivitiesCard key={index} activityData={element} />
                                })
                            }
                            {
                                isActivitiesLoading && <Loader />
                            }
                            {
                                activitiesError && !isActivitiesLoading &&
                                <Error text="C'è stato un problema col caricamento delle attività." message="Ricarica la pagina e riprova" />
                            }
                        </div>
                    </div>

                    {/* Le tue richieste */}
                    <h3 className="mt-8">Le tue richieste</h3>
                    <CustomHr />

                    <div className="w-full my-8 flex flex-col gap-2">
                        {
                            requestActivitiesData && requestActivitiesData.length > 0 && requestActivitiesData.map((element, index) => {
                                return (
                                    <UserRequestCard
                                        key={element.requestId || index}
                                        requestData={element}
                                        allowAdminCancelAccepted
                                        onAdminRequestUpdated={getCompanyData}
                                    />
                                )
                            })
                        }
                    </div>

                </div>
            }
        </PageContainer>
    )
}

export default CompanyDetails